import { MAX_YEAR, MIN_YEAR } from './date-range-filter.utils';
import { LoggerService } from 'src/app/services/logger.service';

/**
 * Card #269: a custom date range stays set on a screen until the user clears it.
 *
 * One localStorage entry per user + workspace + screen, so the Quote list,
 * Payments and Reports each keep their own range, and switching workspace never
 * shows another workspace's range. Only a CUSTOM range is kept: picking a preset
 * forgets it and the screen opens on its own default again.
 * Every key starts with DATE_RANGE_MEMORY_PREFIX, so sign-out removes them all.
 */
export const DATE_RANGE_MEMORY_PREFIX = 'osmos_date_range:';

/** The filter's value while a custom range is applied (Reports and the Quotes list). */
export const CUSTOM_RANGE_VALUE = 'custom_range';
/** Payments has always used its own spelling; its filter is given customValue = this. */
export const PAYMENTS_CUSTOM_RANGE_VALUE = 'customRange';

/** The screens that remember their custom range. */
export const DATE_RANGE_SCREENS = {
  reports: 'reports',
  quoteListing: 'quote-listing',
  payments: 'payments',
} as const;

export interface RememberedDateRange {
  start: Date;
  end: Date;
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

// Stateless and dependency-free, so this plain module can use it without injection.
const logger = new LoggerService();

/**
 * Storage refused an operation. The feature degrades (nothing remembered) but never throws.
 * Logs the operation and the error's name only: keys hold user and workspace ids.
 */
function storageFailed(op: string, e: unknown): void {
  logger.warn(`date-range-memory: ${op} skipped`, (e as Error)?.name ?? 'unknown error');
}

/** The storage to use, or null when the browser refuses it (private mode, blocked site data). */
function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch (e) {
    storageFailed('open storage', e);
    return null;
  }
}

/** Who is signed in to which workspace; null when either is unknown (nothing is saved then). */
function owner(storage: Storage): { userId: string; merchantId: string } | null {
  const usable = (v: unknown): string | null => {
    const s = v == null ? '' : String(v).trim();
    return s && s !== 'null' && s !== 'undefined' ? s : null;
  };
  try {
    let userId = usable(storage.getItem('userId'));
    let merchantId = usable(storage.getItem('merchant_id'));
    if (!userId || !merchantId) {
      const login = JSON.parse(storage.getItem('loginUserValue') || '{}');
      userId = userId || usable(login?.id ?? login?.userId);
      merchantId = merchantId || usable(login?.merchant_id ?? login?.merchantId);
    }
    return userId && merchantId ? { userId, merchantId } : null;
  } catch (e) {
    // Unreadable storage or login value: the owner is unknown, so nothing is saved or restored.
    storageFailed('identify user', e);
    return null;
  }
}

/** The storage key for one screen, or null when the user or workspace is unknown. */
export function dateRangeMemoryKey(screen: string, storage: Storage | null = defaultStorage()): string | null {
  if (!storage || !screen) return null;
  const who = owner(storage);
  return who ? `${DATE_RANGE_MEMORY_PREFIX}${who.userId}:${who.merchantId}:${screen}` : null;
}

/** A local calendar day as YYYY-MM-DD (not toISOString: that would shift the day in some time zones). */
function dayText(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** YYYY-MM-DD back to a local midnight, or null if it is not a real day in the supported years. */
function parseDay(v: unknown): Date | null {
  if (typeof v !== 'string') return null;
  const m = DAY.exec(v);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (y < MIN_YEAR || y > MAX_YEAR) return null;
  const date = new Date(y, mo - 1, d);
  // Feb 31 rolls over to Mar 3: that is not the day that was stored.
  return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d ? date : null;
}

function isValidDate(d: unknown): d is Date {
  return d instanceof Date && !isNaN(d.getTime());
}

/** Remember a custom range for this screen. Does nothing if the range or the owner is unknown. */
export function saveDateRange(screen: string, range: RememberedDateRange | null | undefined, storage: Storage | null = defaultStorage()): void {
  const key = dateRangeMemoryKey(screen, storage);
  if (!key || !range || !isValidDate(range.start) || !isValidDate(range.end)) return;
  const [start, end] = range.start <= range.end ? [range.start, range.end] : [range.end, range.start];
  try {
    storage!.setItem(key, JSON.stringify({ start: dayText(start), end: dayText(end) }));
  } catch (e) {
    // Storage full or blocked: the range still applies, it just won't be remembered.
    storageFailed('save', e);
  }
}

/**
 * The range remembered for this screen, or null. Anything unreadable (bad JSON,
 * an impossible date, an end before the start) is removed, never applied.
 */
export function loadDateRange(screen: string, storage: Storage | null = defaultStorage()): RememberedDateRange | null {
  const key = dateRangeMemoryKey(screen, storage);
  if (!key) return null;
  let raw: string | null = null;
  try {
    raw = storage!.getItem(key);
  } catch (e) {
    storageFailed('load', e);
    return null;
  }
  if (raw == null) return null;
  let parsed: any = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null; // not JSON: treated like any other corrupt value just below (removed)
  }
  const start = parseDay(parsed?.start);
  const end = parseDay(parsed?.end);
  if (!start || !end || end < start) {
    clearDateRange(screen, storage);
    return null;
  }
  return { start, end };
}

/** Forget this screen's range (the user cleared it or picked a preset). */
export function clearDateRange(screen: string, storage: Storage | null = defaultStorage()): void {
  const key = dateRangeMemoryKey(screen, storage);
  if (!key) return;
  try {
    storage!.removeItem(key);
  } catch (e) {
    // Nothing more can be done; the next load re-validates anyway.
    storageFailed('clear', e);
  }
}

/** Sign-out: forget every remembered range, for every user and workspace on this browser. */
export function clearAllSavedDateRanges(storage: Storage | null = defaultStorage()): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith(DATE_RANGE_MEMORY_PREFIX)) keys.push(k);
    }
    keys.forEach(k => storage.removeItem(k));
  } catch (e) {
    storageFailed('clear all', e);
  }
}
